import { lazy, ReactElement, Suspense } from 'react';
import { RouteObject } from 'react-router';
import { Navigate, useLocation, useParams, useRoutes } from 'react-router-dom';

import LegacyChatRedirect from './LegacyChatRedirect';
import PayInvoice from './Agency/PayInvoice';
import AppLogin from './AppLogin';
import LinkExpirePage from './LinkExpire';
import MobileIntro from './MobileIntro';
import PDFGeneration from './PDFGeneration';
import DataDeletionRequest from './DataDeletionRequest';
import QRDownload from './QRDownload';
import RequestAppAccess from './RequestAppAccess';
import ValidationAccount from './ValidationAccount';
import Waitlist from './Waitlist';

import Config from '@/config';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Agency from '@/routes/Agency';
import AgencyCustomerAccountDetail from '@/routes/Agency/Account';
import BackgroundAudit from '@/routes/Agency/BackgroundAudit';
import Compliance from '@/routes/Agency/Compliance';
import AgencyChat from '@/routes/Agency/Chat/Chat';
import ChatV2 from '@/routes/Agency/ChatV2';
import ChatV2Cohort from '@/routes/Agency/ChatV2Cohort';
import AgencyContacts from '@/routes/Agency/Contacts';
import AgencyCreateGigOrder from '@/routes/Agency/CreateGigOrder';
import AgencyCreateOrderV2 from '@/routes/Agency/CreateOrderV2';
import AgencyCustomerDetail from '@/routes/Agency/Customer';
import AgencyCustomerAdminDetail from '@/routes/Agency/CustomerAdmin';
import AgencyCustomers from '@/routes/Agency/Customers';
import AgencyDashboard from '@/routes/Agency/Dashboard';
import DashboardEntry from '@/routes/Agency/DashboardEntry';
import AgencyDashboardV2 from '@/routes/Agency/DashboardV2';
import InvoiceGenerator from '@/routes/Agency/InvoiceGenerator';
import InvoiceHealth from '@/routes/Agency/InvoiceHealth';
import Invoices from '@/routes/Agency/Invoices';
import InvoiceDetail from '@/routes/Agency/Invoices/InvoiceDetailsRevamp';
import InvoicePay from '@/routes/Agency/Invoices/InvoicePay';
import AgencyOrderJobDetail from '@/routes/Agency/Job';
import JobMonitor from '@/routes/Agency/JobMonitor';
import AgencyOrderJobDetailRevamp from '@/routes/Agency/JobOld';
import MyTeam from '@/routes/Agency/MyTeam';
import AgencyOrderDetail from '@/routes/Agency/Order';
import AgencyOrders from '@/routes/Agency/Orders';
import AgencyPayrolls from '@/routes/Agency/Payrolls';
import AgencyPayrollDetail from '@/routes/Agency/Payrolls/PayrollDetail';
import AgencyPayrollsV2 from '@/routes/Agency/PayrollsV2';
import AgencyPayrollDetailV2 from '@/routes/Agency/PayrollsV2/PayrollDetailV2';
import AgencyReports from '@/routes/Agency/Reports';
import Operations from '@/routes/Agency/Operations';
import RestrictedAccess from '@/routes/Agency/RestrictedAccess';
import AgencyWorkers from '@/routes/Agency/Workers';
import GravyWorkersGate from '@/routes/Agency/GravyWorkers/GravyWorkersGate';
import AgencyWorkerV2Detail from '@/routes/Agency/WorkerV2';
import SkillDetailPage from '@/routes/Agency/WorkerV2/SkillDetailPage';
import Authenticated from '@/routes/Authenticated';
import ForgotPassword from '@/routes/ForgotPassword';
import Login from '@/routes/Login';
import PrivateRoute from '@/routes/PrivateRoute';
import ResetPassword from '@/routes/ResetPassword';
import Root from '@/routes/Root';
import SignUp from '@/routes/SignUp';
import Signout from '@/routes/Signout';
import ROUTES from '@/routes/routes';
import { UserRoleEnum } from '@/types/graphql';
import NotificationHub from './Agency/NotificationHub';
import PayrollAudit from './Agency/PayrollAudit';
import WorkbrightAudit from './Agency/WorkBrightAudit';
import ZealAudit from './Agency/ZealAudit';
import HelloSignAudit from './Agency/HelloSignAudit';
import TimesheetHealthChecks from './Agency/TimesheetHealthChecks';
import WorkerExclusionAnalyzer from './Agency/WorkerExclusionAnalyzer';
import OnboardingBypass from './Agency/OnboardingBypass';
import I9Bypass from './Agency/I9Bypass';
import PasswordReset from './Agency/PasswordReset';
import OrderInvites from './Agency/OrderInvites';
import JobDebugger from './Agency/JobDebugger';
import DocumentSigning from './DocumentSigning';
import JobCreator from './JobCreator';
import Posts from './Agency/Posts';
import PostForm from './Agency/Posts/PostForm';
import DocumentsList from './Agency/Documents';
import DocumentForm from './Agency/Documents/DocumentForm';
import DocumentShow from './Agency/Documents/DocumentShow';
import TestSignView from './Agency/Documents/TestSignView';
import AiAssessments from './Agency/AiAssessments';
import AssessmentHealth from './Agency/AssessmentHealth';
import PendingUniforms from './TenantLayout/SkillManagement/Uniforms';
import PendingCertificates from './TenantLayout/SkillManagement/Certificates';
import Analytics from './TenantLayout/Analytics';
import Badges from './TenantLayout/SkillManagement/Badges';
import NoshowAlerts from './Agency/NoshowAlerts';
import SmsLogs from './Agency/SmsLogs';
import TestAnnouncement from './Agency/TestAnnouncement';
import Skills from './Agency/Skills';
import Regions from './Agency/Regions';
import TasksRoutes from './Tasks';
import VersionControls from './Agency/VersionControls';
import ClientSessionsPage from './Agency/ClientSessions';
import ActionsLog from './Agency/ActionsLog';
import FeatureFlags from './Agency/FeatureFlags';
import lifecycleRoute from './Agency/Lifecycle/lifecycleRoute';
import ConnectedFirstResponder from './Agency/FirstResponder/Connected';

// Development-only routes. The require() is non-static and sits inside a
// compile-time guard, so neither production nor debug builds compile the
// module — its contents never reach executable JS or source maps.
let devRoutes: RouteObject[] = [];
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  devRoutes = require('./devRoutes').default;
}

// The app's first React.lazy route. In-house Analytics Data dashboard pulls
// in recharts (+d3, ~130KB gz per the design doc) — a plain top-level import
// would ship that into every CustomerAdmin's main bundle. Split into its own
// async chunk so the cost only lands on the TENANT_ADMINs who open this page.
// No webpack config change is needed for this: dynamic import() already
// triggers webpack 5's default async splitChunks behavior, and
// `chunkFilename` (webpack/build.config.ts) already has a `[name].[hash]`
// pattern in place for exactly this.
const AnalyticsDashboard = lazy(
  () => import('./TenantLayout/AnalyticsDashboard'),
);

// Ops automation console pages, split out like AnalyticsDashboard so recharts
// and the console only load for staff who open it.
const AutomationOverview = lazy(
  () => import('./Agency/AutomationConsole/OverviewPage'),
);
const AutomationClients = lazy(
  () => import('./Agency/AutomationConsole/ClientsPage'),
);
const AutomationComms = lazy(
  () => import('./Agency/AutomationConsole/CommsPage'),
);
const AutomationPools = lazy(
  () => import('./Agency/AutomationConsole/PoolsPage'),
);
const AutomationWatchlist = lazy(
  () => import('./Agency/AutomationConsole/WatchlistPage'),
);

const automationRoute = (path: string, page: ReactElement): RouteObject => ({
  path,
  element: (
    <PrivateRoute
      element={
        <Suspense fallback={<LoadingState text="Loading…" size="2x" />}>
          <Page size="full">{page}</Page>
        </Suspense>
      }
      // graphy's AutomationConsoleController uses require_tenant_admin, which
      // admits helpdesk too; keep the two in step.
      requiredRoles={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.HELPDESK_ADMIN]}
    />
  ),
});

const WorkerDetailRedirect = () => {
  const { workerId } = useParams();
  return <Navigate to={`/workers_v2/${workerId}`} replace />;
};

// Preserves the search string when redirecting from the legacy
// `/orders/create-order-v2` URL to the new `/orders-v2/create` namespace.
const RedirectToOrdersV2Create = () => {
  const { search } = useLocation();
  return (
    <Navigate
      to={`/${ROUTES.ordersV2}/${ROUTES.ordersV2Create}${search}`}
      replace
    />
  );
};

const privateRoutes: RouteObject[] = [
  {
    path: '*',
    element: <Navigate to={ROUTES.basePath} />,
  },
  {
    path: ROUTES.basePath, // "/"
    element: (
      <PrivateRoute
        element={<DashboardEntry />}
        redirectTo={ROUTES.orders}
        requiredRoles={[
          UserRoleEnum.TENANT_ADMIN,
          UserRoleEnum.HELPDESK_ADMIN,
          UserRoleEnum.CUSTOMER_ADMIN,
        ]}
      />
    ),
  },
  {
    // The classic dashboard's own url (GRAVY-10832). "/" redirects tenant
    // admins to Dashboard V2, so "Switch back to dashboard v1" on that page
    // needs a path the redirect leaves alone. Same roles as "/" — this is the
    // same page, just reachable by a name.
    path: ROUTES.dashboardV1,
    element: (
      <PrivateRoute
        element={<AgencyDashboard />}
        redirectTo={ROUTES.orders}
        requiredRoles={[
          UserRoleEnum.TENANT_ADMIN,
          UserRoleEnum.HELPDESK_ADMIN,
          UserRoleEnum.CUSTOMER_ADMIN,
        ]}
      />
    ),
  },
  {
    // Dashboard V2 — the default dashboard for tenant admins as of
    // GRAVY-10832. Reached from the sidebar directly and from "/" via
    // routes/Agency/DashboardEntry; the classic dashboard is still served at
    // "/" for admins who switch back.
    //
    // PERMANENTLY ON. This shipped behind the kill switch
    // `v2.2608.10_dashboard-v2`, which was enabled in production on 2026-08-10
    // and then removed by product decision — the feature is not meant to be
    // switchable. There is consequently no runtime way to turn it off; doing so
    // now means a deploy. The graphy half (`Dashboard::V2Config`) was removed in
    // the same change, so neither side reads the flag any more.
    //
    // TENANT ADMIN ONLY, hard-coded and deliberately not configurable. This
    // previously also admitted HELPDESK_ADMIN and CUSTOMER_ADMIN.
    //
    // The matching restriction is enforced server-side by
    // `API::Admin::DashboardV2::BaseController` (`before_action
    // :require_tenant_admin`), which is what actually locks the feature — this
    // route only decides what the SPA will render, and a client admin could
    // otherwise call `/api/admin/dashboard_v2/*` directly. Change one and you
    // must change the other, or the page renders for someone the API refuses.
    path: ROUTES.dashboardV2,
    element: (
      <PrivateRoute
        element={<AgencyDashboardV2 />}
        redirectTo={ROUTES.orders}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    // Legacy chat URLs — unconditional redirect, bookmarks/history included.
    path: ROUTES.inappchat,
    element: <LegacyChatRedirect />,
  },
  {
    path: ROUTES.chatV2,
    element: <LegacyChatRedirect />,
  },
  {
    // Supabase worker chat (formerly /chat-v2).
    path: ROUTES.inbox,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<ChatV2 />}
            redirectTo="/restricted-access"
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    // Comms canary — Chat V2 cohort management. See/add/remove workers on the
    // Supabase comms allowlist. Ops-only, gated identically to Chat V2.
    path: ROUTES.chatV2Cohort,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<ChatV2Cohort />}
            redirectTo="/restricted-access"
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.orders,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyOrders />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.gigOrderCreate,
        element: (
          <PrivateRoute
            element={<AgencyCreateGigOrder />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        // Legacy `/orders/create-order-v2` URL — redirect to the new
        // `/orders-v2/create` namespace, preserving the query string
        // (the wizard reads `?type=GIG` / `?type=LTA`).
        path: ROUTES.gigOrderCreateV2,
        element: <RedirectToOrdersV2Create />,
      },
      {
        path: ROUTES.orderDetail,
        element: (
          <PrivateRoute
            element={<AgencyOrderDetail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.orderJobDetail,
        element: (
          <PrivateRoute
            element={<AgencyOrderJobDetail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.orderJobDetailOld,
        element: (
          <PrivateRoute
            element={<AgencyOrderJobDetailRevamp />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    // Orders V2 namespace. Today only `create` is wired (the new wizard);
    // future list/detail/templates pages will mount as additional children
    // here. The legacy `/orders/create-order-v2` URL redirects to
    // `/orders-v2/create` — see `RedirectToOrdersV2Create` above.
    path: ROUTES.ordersV2,
    children: [
      {
        path: ROUTES.ordersV2Create,
        element: (
          <PrivateRoute
            element={<AgencyCreateOrderV2 />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.clients,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyCustomers />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.customerDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.customerAccountDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAccountDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.customerAdminDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAdminDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.contacts,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyContacts />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.invoices,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<Invoices />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.invoicesDetail,
        element: (
          <PrivateRoute
            element={<InvoiceDetail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.invoicesPay,
        element: (
          <PrivateRoute
            element={<InvoicePay />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.workers,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyWorkers />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.workerDetail,
        element: <WorkerDetailRedirect />,
      },
    ],
  },
  {
    path: ROUTES.workersV2,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<GravyWorkersGate />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.workerV2Detail,
        element: (
          <PrivateRoute
            element={<AgencyWorkerV2Detail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.workerV2SkillDetail,
        element: (
          <PrivateRoute
            element={<SkillDetailPage />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.myTeam,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<MyTeam />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.myTeamAdminDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAdminDetail />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.myCompany,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyCustomerDetail />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.myCompanyAccountDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAccountDetail />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.chat,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyChat />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.myProfile,
    element: (
      <PrivateRoute
        element={<AgencyCustomerAdminDetail />}
        requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.payrolls,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyPayrolls />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.payrollDetail,
        element: (
          <PrivateRoute
            element={<AgencyPayrollDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.payrollsV2,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyPayrollsV2 />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.payrollDetailV2,
        element: (
          <PrivateRoute
            element={<AgencyPayrollDetailV2 />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.payrollAudit,
    element: (
      <PrivateRoute
        element={<PayrollAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.backgroundAudit,
    element: (
      <PrivateRoute
        element={<BackgroundAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.orderInvites,
    element: (
      <PrivateRoute
        element={<OrderInvites />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.compliance,
    element: (
      <PrivateRoute
        element={<Compliance />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.workbrightAudit,
    element: (
      <PrivateRoute
        element={<WorkbrightAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.zealAudit,
    element: (
      <PrivateRoute
        element={<ZealAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.hellosignAudit,
    element: (
      <PrivateRoute
        element={<HelloSignAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.timesheetHealthChecks,
    element: (
      <PrivateRoute
        element={<TimesheetHealthChecks />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.jobDebugger,
    element: (
      <PrivateRoute
        element={<JobDebugger />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.CUSTOMER_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.workerExclusionAnalyzer,
    element: (
      <PrivateRoute
        element={<WorkerExclusionAnalyzer />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.invoiceHealth,
    element: (
      <PrivateRoute
        element={<InvoiceHealth />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.reports,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyReports />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    // GRAVY-10878: Admin › Operations (ops duty schedule).
    path: ROUTES.operations,
    element: (
      <PrivateRoute
        element={<Operations />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.jobmonitor,
    element: (
      <PrivateRoute
        element={<JobMonitor />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.HELPDESK_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.invoiceGenerator,
    element: (
      <PrivateRoute
        element={<InvoiceGenerator />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.notificationHub,
    element: (
      <PrivateRoute
        element={<NotificationHub />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.onboardingBypass,
    element: (
      <PrivateRoute
        element={<OnboardingBypass />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.i9Bypass,
    element: (
      <PrivateRoute
        element={<I9Bypass />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.passwordReset,
    element: (
      <PrivateRoute
        element={<PasswordReset />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.posts,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<Posts />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.postCreate,
        element: (
          <PrivateRoute
            element={<PostForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.postEdit,
        element: (
          <PrivateRoute
            element={<PostForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.documents,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<DocumentsList />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentCreate,
        element: (
          <PrivateRoute
            element={<DocumentForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentShow,
        element: (
          <PrivateRoute
            element={<DocumentShow />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentEdit,
        element: (
          <PrivateRoute
            element={<DocumentForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentTestSign,
        element: (
          <PrivateRoute
            element={<TestSignView />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.aiAssessments,
    element: (
      <PrivateRoute
        element={<AiAssessments />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.assessmentHealth,
    element: (
      <PrivateRoute
        element={<AssessmentHealth />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.skills,
    element: (
      <PrivateRoute
        element={<Skills />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.regions,
    element: (
      <PrivateRoute
        element={<Regions />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.noshowAlerts,
    element: (
      <PrivateRoute
        element={<NoshowAlerts />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.smsLogs,
    element: (
      <PrivateRoute
        element={<SmsLogs />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.testAnnouncement,
    element: (
      <PrivateRoute
        element={<TestAnnouncement />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.testAnnouncementV2,
    element: (
      <PrivateRoute
        element={<TestAnnouncement variant="supabase" />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.versionControls,
    element: (
      <PrivateRoute
        element={<VersionControls />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: `${ROUTES.tasks}/*`,
    element: (
      <PrivateRoute
        element={<TasksRoutes />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    // Hidden fleet view of worker client sessions. No sidebar entry — reachable
    // only by typing the URL directly.
    path: ROUTES.clientSessions,
    element: (
      <PrivateRoute
        element={<ClientSessionsPage />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    // Hidden internal Actions Log (shift_events feed). No sidebar entry —
    // reachable only by typing the URL directly. Tenant-admin only.
    path: ROUTES.actionsLog,
    element: (
      <PrivateRoute
        element={<ActionsLog />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    // Feature Flags & Experiments — ops UI for graphy's native A/B engine.
    // Has a sidebar entry (gated the same way as AI Assessments/Analytics),
    // unlike ActionsLog/ClientSessions above. Tenant-admin only.
    path: ROUTES.featureFlags,
    element: (
      <PrivateRoute
        element={<FeatureFlags />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  lifecycleRoute,
  {
    // First Responder — GRAVY-10373 canonical UI convergence: this is now the ONE production
    // console (the frozen, Alex/Virna-approved presentation wired to real backend data).
    // Deliberately NOT in the sidebar during the pilot — direct URL only, like
    // ActionsLog/ClientSessions. TenantAdmin operates it; Helpdesk can open it read-only
    // (GRAVY-10816 AC-6: helpdesk can read the console, cannot tick, change the mode or edit a
    // policy — the console hides those controls for that role and the backend's
    // require_tenant_admin_role refuses the writes regardless).
    // The former separate connected-console route (and its own IS_QA-gated registration) is
    // retired — its container now lives here, absorbed rather than duplicated. See
    // ROUTE_RETIREMENT_PLAN.md.
    path: ROUTES.firstResponder,
    element: (
      <PrivateRoute
        element={<ConnectedFirstResponder />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.HELPDESK_ADMIN]}
      />
    ),
  },
  ...devRoutes,
  {
    path: ROUTES.restrictedAccess,
    element: (
      <PrivateRoute
        element={<RestrictedAccess />}
        requiredRoles={[
          UserRoleEnum.TENANT_ADMIN,
          UserRoleEnum.HELPDESK_ADMIN,
          UserRoleEnum.CUSTOMER_ADMIN,
        ]}
      />
    ),
  },
  {
    path: ROUTES.skillManagement,
    children: [
      {
        path: ROUTES.pendingUniforms,
        element: (
          <PrivateRoute
            element={<PendingUniforms />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.pendingCertificates,
        element: (
          <PrivateRoute
            element={<PendingCertificates />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        // Badge/certificate CATALOG management. Distinct from
        // `pendingCertificates` above, which is photo verification.
        path: ROUTES.badges,
        element: (
          <PrivateRoute
            element={<Badges />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.analytics,
    element: (
      <PrivateRoute
        element={<Analytics />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    // Analytics Data — read-only ops dashboard over the in-house
    // AnalyticsEvent pipeline. A NEW, separate page from the Vexo console
    // above (ROUTES.analytics); sidebar entry gated the same way (see
    // roles.json's "nav-analytics-dashboard-link").
    path: ROUTES.analyticsDashboard,
    element: (
      <PrivateRoute
        element={
          <Suspense
            fallback={<LoadingState text="Loading analytics…" size="2x" />}
          >
            <AnalyticsDashboard />
          </Suspense>
        }
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  automationRoute(ROUTES.automationOverview, <AutomationOverview />),
  automationRoute(ROUTES.automationClients, <AutomationClients />),
  automationRoute(ROUTES.automationComms, <AutomationComms />),
  automationRoute(ROUTES.automationPools, <AutomationPools />),
  automationRoute(ROUTES.automationWatchlist, <AutomationWatchlist />),
];

const publicRoutes: RouteObject[] = [
  {
    path: ROUTES.linkExpire,
    element: <LinkExpirePage />,
  },
  {
    path: ROUTES.payInvoice,
    element: <PayInvoice />,
  },
  {
    path: ROUTES.mobileIntro,
    element: <MobileIntro />,
  },
  {
    path: ROUTES.appLogin,
    element: <AppLogin />,
  },
  {
    path: ROUTES.requestAppAccess,
    element: <RequestAppAccess />,
  },
  {
    path: ROUTES.waitlist,
    element: <Waitlist />,
  },
  {
    path: ROUTES.pdf_generation,
    element: <PDFGeneration />,
  },
  {
    path: ROUTES.qrDownload,
    element: <QRDownload />,
  },
  {
    path: ROUTES.dataDeletionRequest,
    element: <DataDeletionRequest />,
  },
  { path: ROUTES.login, element: <Login /> },
  { path: ROUTES.signUp, element: <SignUp /> },
  { path: ROUTES.signOut, element: <Signout /> },
  { path: ROUTES.forgotPassword, element: <ForgotPassword /> },
  { path: ROUTES.resetPassword, element: <ResetPassword /> },
  { path: ROUTES.validationAccount, element: <ValidationAccount /> },
  {
    path: ROUTES.jobCreator,
    element: <JobCreator />,
  },
  {
    path: ROUTES.documentSigning,
    element: <DocumentSigning />,
  },
];

const tenantAdminRoutes: RouteObject[] = [
  // ... existing code ...
];

const routes: RouteObject[] = [
  {
    path: Config.BASEPATH,
    children: [
      // Document signing route is independent of Root/admin session
      // so the webview session doesn't expire as long as the external API token is valid
      {
        path: ROUTES.documentSigning,
        element: <DocumentSigning />,
      },
      {
        element: <Root />,
        children: [
          {
            element: <Authenticated />,
            children: [{ element: <Agency />, children: [...privateRoutes] }],
          },
          { children: [...publicRoutes] },
        ],
      },
    ],
  },
];

const AppRouter = () => {
  const routing = useRoutes(routes);

  return routing;
};

export default AppRouter;
